/**
 * Help, at the top right of every screen (Rook, 2026-10-19, ADR-050): V1's Help button and its
 * Help & Emergencies, as a sheet that slides up over whatever the guest was doing. The cards are
 * the guide's Help tab, edited in Ops → Guest App Content; a card with a phone number also gets
 * Text us, and the alert-icon card is drawn as the emergency one. Between them, quick fixes for
 * what guests most often get stuck on: the door, the Wi-Fi, finding something.
 *
 * The sheet is open while the address has `?help`, so the phone's Back button closes it.
 */
import { HostNote, Icon, SheetModal, type IconName } from '@pmh/shared';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { doorPage, GuideText, linkProps, sectionTitle } from '../lib/guide';
import { PushLink } from '../lib/nav';
import { useContent, useHome, useHouseBasics } from '../lib/queries';
import { useSession } from '../lib/session';
import type { GuideEntry } from '../lib/types';
import { Details, EntryIcon } from './GuideParts';

interface HelpState {
  helpSheet?: boolean;
}

/** Open and close the sheet through the address, so Back closes it. */
export function useHelpSheet() {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const open = params.has('help');
  const leaving = useRef(false);
  useEffect(() => {
    if (!open) leaving.current = false;
  }, [open]);
  const show = () =>
    setParams(
      (p) => {
        const next = new URLSearchParams(p);
        next.set('help', '1');
        return next;
      },
      { preventScrollReset: true, state: { helpSheet: true } satisfies HelpState },
    );
  // The dialog reports Escape twice (cancel, then close): only the first one counts.
  const hide = () => {
    if (!open || leaving.current) return;
    leaving.current = true;
    if ((location.state as HelpState | null)?.helpSheet) void navigate(-1);
    else
      setParams(
        (p) => {
          const next = new URLSearchParams(p);
          next.delete('help');
          return next;
        },
        { replace: true, preventScrollReset: true },
      );
  };
  return { open, show, hide };
}

export function HelpButton({ tone = 'cream' }: { tone?: 'cream' | 'pine' }) {
  const sheet = useHelpSheet();
  return (
    <>
      <button type="button" className={`g-helpbtn g-helpbtn--${tone}`} aria-haspopup="dialog" onClick={sheet.show}>
        <Icon name="lifebuoy" size={18} strokeWidth={2.2} />
        Help
      </button>
      <HelpSheet open={sheet.open} onClose={sheet.hide} />
    </>
  );
}

const digits = (link: string) => link.replace(/[^\d+]/g, '');

/** The tel: number on a card, unless it's 911. */
function hostPhone(e: GuideEntry): string | null {
  if (!e.link?.startsWith('tel:')) return null;
  const n = digits(e.link);
  return n.length >= 7 ? n : null;
}

/** "Text us" goes where the rest of the app texts (settings' Text us), or to the card's number. */
function smsFor(e: GuideEntry, textUs: string | undefined): string | null {
  const phone = hostPhone(e);
  if (!phone) return null;
  const t = (textUs ?? '').trim();
  const own = digits(t);
  return !/^\[.*\]$/.test(t) && !t.includes('@') && own.length >= 7 ? `sms:${own}` : `sms:${phone}`;
}

function HelpCard({ e, textUs }: { e: GuideEntry; textUs?: string }) {
  const sos = e.icon === 'alert';
  const sms = sos ? null : smsFor(e, textUs);
  return (
    <li className={`g-entry g-entry--card g-helpcard${sos ? ' g-helpcard--sos' : ''}`}>
      <div className="g-entry__cardhead">
        <EntryIcon name={e.icon ?? 'phone'} tone={0} />
        <span className="g-entry__titles">
          <span className="g-entry__title">{e.title}</span>
          {e.subtitle && <span className="g-entry__sub">{e.subtitle}</span>}
        </span>
      </div>
      <Details e={{ ...e, link: null }} />
      {(e.link || sms) && (
        <div className="g-helpcard__btns">
          {/* Texting is how guests reach us everywhere else, so it leads; then the card's own button. */}
          {sms && (
            <a className="pmh-btn pmh-btn--md pmh-btn--primary" href={sms}>
              <Icon name="chat" size={18} />
              Text us
            </a>
          )}
          {e.link && (
            <a className={`pmh-btn pmh-btn--md ${sos ? 'g-btn-sos' : 'pmh-btn--secondary'}`} {...linkProps(e.link)}>
              <Icon name={e.link.startsWith('tel:') ? 'phone' : 'arrowRight'} size={18} />
              {e.linkLabel || (e.link.startsWith('tel:') ? 'Call' : 'More info')}
            </a>
          )}
        </div>
      )}
    </li>
  );
}

function Quick({ to, icon, title, sub }: { to: string; icon: IconName; title: string; sub?: string }) {
  return (
    <li>
      <PushLink to={to} replace className="g-quick">
        <Icon name={icon} size={20} />
        <span className="g-quick__text">
          <span className="g-quick__title">{title}</span>
          {sub && <span className="g-quick__sub">{sub}</span>}
        </span>
        <Icon name="chevronRight" size={18} className="g-quick__end" />
      </PushLink>
    </li>
  );
}

function WifiQuick({ network, password }: { network: string; password: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <li>
      <button
        type="button"
        className="g-quick"
        onClick={() => void navigator.clipboard?.writeText(password).then(() => setCopied(true), () => undefined)}
      >
        <Icon name="wifi" size={20} />
        <span className="g-quick__text">
          <span className="g-quick__title">Wi-Fi: {network}</span>
          <span className="g-quick__sub">
            {password} · {copied ? 'copied' : 'tap to copy'}
          </span>
        </span>
        <Icon name={copied ? 'check' : 'copy'} size={18} className="g-quick__end" />
      </button>
    </li>
  );
}

const placeholder = (v: string) => /^\[.*\]$/.test(v.trim());

export function HelpSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const content = useContent();
  const house = useHouseBasics();
  const { token } = useSession();
  const home = useHome();
  const help = content.data?.help;
  const entries = help?.sections.flatMap((s) => s.groups.flatMap((g) => g.entries)) ?? [];
  const door = doorPage(content.data);
  const wifi = token && home.data?.registered ? home.data.wifi : null;
  const showWifi = wifi && !placeholder(wifi.network) && !placeholder(wifi.password);
  const card = (e: GuideEntry) => <HelpCard key={e.id} e={e} textUs={house?.textUs} />;
  return (
    <SheetModal open={open} onClose={onClose} title={help?.title || 'Help'}>
      <div className="g-help">
        {help?.intro && (
          <HostNote size={22} color="pine">
            {help.intro}
          </HostNote>
        )}
        <ul className="g-entries g-entries--cards">{entries.filter((e) => e.icon !== 'alert').map(card)}</ul>
        <div className="g-help__quick" role="group" aria-label="Quick fixes">
          <span className="g-kicker">Quick fixes</span>
          <ul className="g-quicklist">
            {showWifi && <WifiQuick network={wifi.network} password={wifi.password} />}
            {door && <Quick to={door} icon="key" title="Getting in" sub="The door code and the lock" />}
            <Quick to="/house?s=where" icon="search" title="Can't find something?" sub={sectionTitle(content.data, 'where', 'Where to Find Stuff')} />
            <Quick to="/house?s=how-to" icon="bolt" title="How does this work?" sub={sectionTitle(content.data, 'how-to', 'How to Use Stuff')} />
          </ul>
        </div>
        <ul className="g-entries g-entries--cards">{entries.filter((e) => e.icon === 'alert').map(card)}</ul>
        {entries.length === 0 && !content.isLoading && (
          <GuideText text="Something not right? Text us and we'll sort it out." />
        )}
      </div>
    </SheetModal>
  );
}
